import { useState, useEffect } from "react";
import axios from "axios";
import { mutate } from "swr";
import { Button, List, TextField } from "@mui/material";
import { Add } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import localize from "../../../common/localize";
import {
  GetDateInYYYYMMDDHHmmssUTC,
} from "../../../common/utils";
import useGlobalContext from "../../../hooks/useGlobalContext";
import Combobox from "../../../components/common/Combobox";
import {
  PopperFullWidth,
  getInputLabelProps,
  getInputProps,
} from "../../../common/appStyles";
import ErrorMessage from "../../../components/common/ErrorMessage";
import { getClearClientResolutionModel } from "../../../common/EntitiesManagement";
import ModalBackground from "../../../components/common/ModalBackground";
import NotificationItem from "../../../components/common/NotificationItem";
import ResolutionItem from "../../resolution/components/ResolutionItem";

export default function ClientResolutions() {
  const {
    clientModel,
    createEntity,
    deleteEntity,
    CLIENTS_API,
    RESOLUTION_ENDPOINT,
    config,
    clientResolutions,
    getClientResolutions,
  } = useGlobalContext();
  const [resolutionData, setResolutionData] = useState(null);
  const [selectedResolution, setSelectedResolution] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [clientResolutionsData, setclientResolutionsData] =
    useState(clientResolutions);
  const [showdateModal, setShowDateModal] = useState(false);
  const [clientResolutionModel, setClientResolutionModel] = useState(
    getClearClientResolutionModel()
  );

  const getResolutionsData = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${RESOLUTION_ENDPOINT}/`, requestConfig);

    setResolutionData(data?.data);
  };

  useEffect(() => {
    getResolutionsData();
  }, []);

  const requestClientResolutions = async (clientId) => {
    setclientResolutionsData(await getClientResolutions(clientId));
  };

  useEffect(() => {
    if (clientModel) {
      requestClientResolutions(clientModel.Id);
    }
  }, [clientModel]);

  const validateOperationResult = async (result) => {
    setShowDateModal(false);
    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([CLIENTS_API, config]);
    requestClientResolutions(clientModel.Id);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${CLIENTS_API}`}/${clientModel.Id}/resolution`,
      {
        ...getClearClientResolutionModel(),
        AssignmentDate: GetDateInYYYYMMDDHHmmssUTC(
          new Date(clientResolutionModel.AssignmentDate)
        ),
        ClientId: clientModel.Id,
        ResolutionId: data.Id,
      },
      config
    );
    validateOperationResult(updateResult);
  };

  const handleResolutionSelection = (selected) => {
    setSelectedResolution(selected);
  };

  const handleAddBtn = async () => {
    setShowDateModal(true);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${CLIENTS_API}/${clientModel.Id}/resolution/${itemId}`}`,
      config
    );
    validateOperationResult(deleteResult);
  };

  const handleAssignmentDateChange = (newValue) => {
    if (!newValue) {
      setClientResolutionModel({
        ...clientResolutionModel,
        AssignmentDate: null,
      });
      return;
    }

    setClientResolutionModel({
      ...clientResolutionModel,
      AssignmentDate: newValue,
    });
  };

  const isResolutionSelected = () => {
    if (selectedResolution) {
      return true;
    }

    return false;
  };

  return (
    <div style={{ width: "100%" }}>
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      <section className="flex items-center w-full gap-2 mb-3">
        <div style={{ flex: 1 }}>
          <Combobox
            fieldLabel={localize.resolution}
            fieldValue={selectedResolution}
            fieldOptions={resolutionData?.Data ?? []}
            popperFullWidth={PopperFullWidth}
            customOptionLabel={(option) =>
              option.Code ? `${option.Code}-${option.Name}` : option.Name
            }
            handleValueChange={handleResolutionSelection}
            id="ResolutionSelection"
          />
        </div>

        <div>
          <Button
            disabled={!isResolutionSelected()}
            size="small"
            variant="contained"
            color="success"
            className="app_general_button_data app_success_button btn-sm"
            onClick={handleAddBtn}
          >
            <span className="mr-2">{localize.add}</span>
            <Add className="text-lg" />
          </Button>
        </div>
      </section>

      {showdateModal && (
        <ModalBackground
          okButtonText={localize.assign}
          onClickToCancel={() => {
            setShowDateModal(false);
          }}
          onClickToSubmit={() => {
            handleCreateRequest(selectedResolution);
            setSelectedResolution(undefined);
          }}
          setShowModal={setShowDateModal}
          showModal={showdateModal}
          title={localize.assignment_date}
          Component={
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={localize.assignment_date}
                className="w-full"
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                PopperComponent={PopperFullWidth}
                onChange={(date) => {
                  handleAssignmentDateChange(date);
                }}
                value={clientResolutionModel?.AssignmentDate}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      placeholder: "",
                    }}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      style: { fontSize: "1em", paddingBottom: "10px" },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          }
        />
      )}

      {
        <List className="w-full flex flex-col gap-2">
          {clientResolutionsData?.length > 0 ? (
            clientResolutionsData.map((item) => (
              <ResolutionItem
                resolution={item}
                onDelete={() => {
                  handleDeleteRequest(item.Resolution.Id);
                }}
              />
            ))
          ) : (
            <NotificationItem notification={{ message: localize.no_data }} />
          )}
        </List>
      }
    </div>
  );
}
